import React from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  QR_CODE_TYPE,
  QR_CRYPTO_CURRENCIES,
  QR_CRYPTO_OPTIONS,
  QR_EMAIL_OPTIONS,
  QR_SMS_OPTIONS,
  QR_VCARD_OPTIONS,
  QRCodeType,
} from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { Btc16, Eth16 } from '@makyoapp/makyo-icon/icon'
import { Chip, SelectDropdown } from '@makyoapp/makyo-ui'

import { useCreateColumnContext } from '@/context/CreateColumnContext'

import { ColumnDd } from './ColumnDd'
import { Fld, Lbl } from './shared'

/**
 * handle vcard fields for qrcode column type
 */
function FieldVCard() {
  const { id, value, setValue } = useCreateColumnContext()

  return (
    <>
      {QR_VCARD_OPTIONS.map((qrOpt) => {
        return (
          <Fld key={qrOpt.id}>
            <Lbl required={qrOpt.required}>
              <Trans>{qrOpt.label}</Trans>
            </Lbl>
            <ColumnDd
              types={qrOpt.colTypes}
              id={`${id}-qr-vcard-${qrOpt.id}`}
              selected={value.qrOptions.qrVCard[qrOpt.id]}
              onChange={(opt) => {
                setValue({
                  qrOptions: {
                    ...value.qrOptions,
                    qrVCard: {
                      ...value.qrOptions.qrVCard,
                      [qrOpt.id]: opt.id,
                    },
                  },
                })
              }}
            />
          </Fld>
        )
      })}
    </>
  )
}

/**
 * handle SMS fields for qrcode column type
 */
function FieldSms() {
  const { id, value, setValue } = useCreateColumnContext()

  return (
    <>
      {QR_SMS_OPTIONS.map((qrOpt) => {
        return (
          <Fld key={qrOpt.id}>
            <Lbl required={qrOpt.id === 'to'}>
              <Trans>{qrOpt.label}</Trans>
            </Lbl>
            <ColumnDd
              types={qrOpt.colTypes}
              id={`${id}-qr-vcard-${qrOpt.id}`}
              selected={value.qrOptions.qrSMS[qrOpt.id]}
              onChange={(opt) => {
                setValue({
                  qrOptions: {
                    ...value.qrOptions,
                    qrSMS: {
                      ...value.qrOptions.qrSMS,
                      [qrOpt.id]: opt.id,
                    },
                  },
                })
              }}
            />
          </Fld>
        )
      })}
    </>
  )
}

/**
 * handle email fields for qrcode column type
 */
function FieldEmail() {
  const { id, value, setValue } = useCreateColumnContext()

  return (
    <>
      {QR_EMAIL_OPTIONS.map((qrOpt) => {
        return (
          <Fld key={qrOpt.id}>
            <Lbl required={qrOpt.id === 'to'}>
              <Trans>{qrOpt.label}</Trans>
            </Lbl>
            <ColumnDd
              types={qrOpt.colTypes}
              id={`${id}-qr-vcard-${qrOpt.id}`}
              selected={value.qrOptions.qrEmail[qrOpt.id]}
              onChange={(opt) => {
                setValue({
                  qrOptions: {
                    ...value.qrOptions,
                    qrEmail: {
                      ...value.qrOptions.qrEmail,
                      [qrOpt.id]: opt.id,
                    },
                  },
                })
              }}
            />
          </Fld>
        )
      })}
    </>
  )
}

/**
 * handle URL fields for qrcode column type
 */
function FieldURL() {
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <Fld>
      <Lbl>
        <Trans>URL</Trans>
      </Lbl>
      <ColumnDd
        id={`${id}-qr-url`}
        types={[ColumnTypes.URL]}
        onChange={(opt) => {
          setValue({
            qrOptions: {
              ...value.qrOptions,
              qrURL: opt.id,
            },
          })
        }}
        selected={value.qrOptions.qrURL}
      />
    </Fld>
  )
}

/**
 * handle text fields for qrcode column type
 */
function FieldText() {
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <Fld>
      <Lbl>
        <Trans>Text</Trans>
      </Lbl>
      <ColumnDd
        id={`${id}-qr-text`}
        onChange={(opt) => {
          setValue({
            qrOptions: {
              ...value.qrOptions,
              qrText: opt.id,
            },
          })
        }}
        selected={value.qrOptions.qrText}
      />
    </Fld>
  )
}

/**
 * handle file/attachment fields for qrcode column type
 */
function FieldFile() {
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <Fld>
      <Lbl>
        <Trans>File</Trans>
      </Lbl>
      <ColumnDd
        id={`${id}-qr-file`}
        types={[ColumnTypes.Attachment]}
        onChange={(opt) => {
          setValue({
            qrOptions: {
              ...value.qrOptions,
              qrFile: opt.id,
            },
          })
        }}
        selected={value.qrOptions.qrFile}
      />
    </Fld>
  )
}

/**
 * handle cryptocurrency fields for qrcode column type
 */
function FieldCrypto() {
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <>
      <Fld>
        <Lbl>
          <Trans>Currency</Trans>
        </Lbl>
        {QR_CRYPTO_CURRENCIES.map((cry) => {
          return (
            <Chip
              key={cry}
              id={`${id}-qr-crypto-${cry}`}
              active={value.qrOptions.qrCrypto.crypto === cry}
              className='cursor-pointer'
              onClick={() => {
                setValue({
                  qrOptions: {
                    ...value.qrOptions,
                    qrCrypto: {
                      ...value.qrOptions.qrCrypto,
                      crypto: cry,
                    },
                  },
                })
              }}
            >
              {cry === 'bitcoin' ? (
                <>
                  <Btc16 width={24} height={24} />
                  <span>BTC</span>
                </>
              ) : (
                <>
                  <Eth16 width={24} height={24} />
                  <span>ETH</span>
                </>
              )}
            </Chip>
          )
        })}
      </Fld>
      {QR_CRYPTO_OPTIONS.map((qrOpt) => {
        return (
          <Fld key={qrOpt.id}>
            <Lbl>
              <Trans>{qrOpt.label}</Trans>
            </Lbl>
            <ColumnDd
              types={qrOpt.colTypes}
              id={`${id}-qr-crypto-${qrOpt.id}`}
              selected={value.qrOptions.qrCrypto[qrOpt.id]}
              onChange={(opt) => {
                setValue({
                  qrOptions: {
                    ...value.qrOptions,
                    qrCrypto: {
                      ...value.qrOptions.qrCrypto,
                      [qrOpt.id]: opt.id,
                    },
                  },
                })
              }}
            />
          </Fld>
        )
      })}
    </>
  )
}

/**
 * handle qrcode type field for qrcode column type
 */
function FieldQRCodeType() {
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <Fld>
      <Lbl>
        <Trans>QR Code Type</Trans>
      </Lbl>
      <SelectDropdown
        id={`${id}-qr-type`}
        menuDefaultSize={false}
        menuClassName={'max-h-[220px] overflow-y-auto'}
        menuContainerClassName='mk-ag-sdd'
        menuZIndex='z-305'
        portalMenu
        onChange={(opt) => {
          setValue({
            qrOptions: {
              ...value.qrOptions,
              qrType: opt.id,
            },
          })
        }}
        value={QR_CODE_TYPE.find((qrt) => qrt.id === value.qrOptions.qrType)}
        optionLabel={(opt) => opt.label}
        renderOption={(opt) => {
          const Ic = Icon[opt.icon as unknown as keyof typeof Icon]
          return (
            <div className='flex-c'>
              <Ic />
              <div>{opt.label}</div>
            </div>
          )
        }}
        options={QR_CODE_TYPE}
      />
    </Fld>
  )
}

/**
 * handle all fields for qrcode column type
 */
export default function FieldQRCode() {
  const { value } = useCreateColumnContext()
  return (
    <>
      <FieldQRCodeType />
      {value.qrOptions.qrType === QRCodeType.VCard && <FieldVCard />}
      {value.qrOptions.qrType === QRCodeType.SMS && <FieldSms />}
      {value.qrOptions.qrType === QRCodeType.Email && <FieldEmail />}
      {value.qrOptions.qrType === QRCodeType.Text && <FieldText />}
      {value.qrOptions.qrType === QRCodeType.URL && <FieldURL />}
      {value.qrOptions.qrType === QRCodeType.Crypto && <FieldCrypto />}
      {value.qrOptions.qrType === QRCodeType.File && <FieldFile />}
    </>
  )
}
